/**
 * The preview frame protocol: what the parent page (the editor's Preview panel, the framed harness page) and the
 * sandboxed frame (`sandbox="allow-scripts"`, opaque origin) say to each other with postMessage.
 *
 * The parent sends package files as ArrayBuffers; the frame makes its own blob: URLs from them and passes every
 * package path to the graphic as `v_assetUrls`, the way PRISM's OGraf host does (docs/prism-handover.md). Nothing
 * else crosses the boundary: no tokens, no cookies, no access to the parent's DOM.
 */
import type { CallRecord, Inspection, LoadedPackage } from '../page/types.ts';

export const FRAME_PROTOCOL = 'prism-graphics-preview';
export const FRAME_PROTOCOL_VERSION = 1;

interface Base {
  protocol: typeof FRAME_PROTOCOL;
  version: typeof FRAME_PROTOCOL_VERSION;
}

/** Parent → frame. */
export type ToFrame = Base &
  (
    | { type: 'hello' }
    | { type: 'load-files'; id: number; files: Record<string, ArrayBuffer>; manifestPath: string; size?: { width: number; height: number } }
    | { type: 'load-url'; id: number; manifestUrl: string; blobUrls: boolean }
    | { type: 'calls'; calls: Array<{ id: number; method: string; params?: unknown }> }
    | { type: 'inspect'; id: number }
    | { type: 'remove'; id: number }
  );

/** Frame → parent. */
export type FromFrame = Base &
  (
    | { type: 'ready' }
    | { type: 'reply'; id: number; ok: true; value: unknown }
    | { type: 'reply'; id: number; ok: false; error: string }
    | { type: 'call-start'; record: CallRecord }
    | { type: 'call-end'; record: CallRecord }
    | { type: 'console'; level: 'error' | 'warn'; text: string }
  );

export type ReplyValue = LoadedPackage | Inspection | null;

export function isProtocolMessage(m: unknown): m is { protocol: string; version: number; type: string } {
  return (
    typeof m === 'object' &&
    m !== null &&
    (m as { protocol?: unknown }).protocol === FRAME_PROTOCOL &&
    (m as { version?: unknown }).version === FRAME_PROTOCOL_VERSION &&
    typeof (m as { type?: unknown }).type === 'string'
  );
}

export const header = { protocol: FRAME_PROTOCOL, version: FRAME_PROTOCOL_VERSION } as const;

/** MIME types for package files (the frame labels its blob: URLs with them; the harness server serves them). */
export const MIME: Record<string, string> = {
  '.html': 'text/html; charset=utf-8',
  '.js': 'text/javascript; charset=utf-8',
  '.mjs': 'text/javascript; charset=utf-8',
  '.json': 'application/json; charset=utf-8',
  '.css': 'text/css; charset=utf-8',
  '.woff2': 'font/woff2',
  '.woff': 'font/woff',
  '.ttf': 'font/ttf',
  '.otf': 'font/otf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.svg': 'image/svg+xml',
  '.webm': 'video/webm',
  '.mp4': 'video/mp4',
  '.zip': 'application/zip',
  '.txt': 'text/plain; charset=utf-8',
};

export function mimeFor(path: string): string {
  const m = /\.[^./]+$/.exec(path.toLowerCase());
  return (m && MIME[m[0]]) || 'application/octet-stream';
}
